import { type FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu as AntMenu, Badge, Popconfirm } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { observer } from 'mobx-react-lite';

import { TaskTypes, type JsonDataItem, type WithOptionalDataTest } from 'types';
import { Notepad, PopconfirmDescription, useVoipMutation } from 'components';
import { BookingStore, RootStore } from 'store';
import { postVoipLogout } from 'queries';
import { menuConfig, getMenuState } from './menu.config';
import styles from './menu.module.scss';
import { useTaskCount } from 'hooks/useTaskCount';

type Props = {
  isCollapsed?: boolean;
} & WithOptionalDataTest;

export const Menu: FC<Props> = observer((props) => {
  const { isCollapsed, dataTestId } = props;
  const { step, resetBookingProgress } = BookingStore;
  const { menu, setMenu, resetAppData } = RootStore;
  const { logout } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [modalRedirectKey, setModalRedirectKey] = useState<string>('');
  const { mutate: logoutVoip } = useVoipMutation(postVoipLogout);

  const { taskCounts: taskCounter } = useTaskCount();

  type MenuItem = {
    label: string;
    key: string;
    icon: React.ReactNode;
    children?: MenuItem[];
    disabled?: boolean;
  };

  const getBadge = (key: string) => {
    const getCount = (types: TaskTypes[]) =>
      taskCounter?.reduce((sum, item) => sum + (types.includes(item.ID) ? item.count : 0), 0) ?? 0;
    const countMap: { [key: string]: number | undefined } = {
      '/tasks/tickets': getCount([TaskTypes.Tickets, TaskTypes.TicketBusinessSupport]),
      '/tasks/callbacks': getCount([TaskTypes.Callbacks]),
    };

    if (key === 'tasks-group' && taskCounter) {
      return taskCounter?.some((task: JsonDataItem) => task.count !== 0) && <Badge status="error" />;
    }

    return countMap[key] ? <Badge count={countMap[key]} size="small" /> : null;
  };

  const transformMenu = (menu: any): MenuItem[] => {
    return menu?.map((item: JsonDataItem) => {
      const { label, children, key, ...rest } = item;

      if (!children) {
        return {
          ...rest,
          key,
          label: (
            <>
              {step && step < 4 ? (
                <Popconfirm
                  title="Alert"
                  onConfirm={() => {
                    handleLeaveBooking();
                  }}
                  description={<PopconfirmDescription />}
                  okText="Yes"
                >
                  {item.label} {getBadge(item.key)}
                </Popconfirm>
              ) : (
                <div onClick={() => resetBookingProgress()}>
                  {item.label} {getBadge(item.key)}
                </div>
              )}
            </>
          ),
        };
      } else {
        return {
          ...rest,
          key,
          label: (
            <>
              {label} {getBadge(key)}
            </>
          ),
          children: transformMenu(children),
        };
      }
    });
  };

  const handleLogout = () => {
    logoutVoip();
    void logout({ logoutParams: { returnTo: window.location.origin } });
    resetBookingProgress();
    resetAppData();
  };

  const handleLeaveBooking = () => {
    modalRedirectKey === '/logout' ? handleLogout() : navigate(modalRedirectKey);
    resetBookingProgress();
  };

  useEffect(() => {
    // update menu highlighting
    setMenu(getMenuState(location.pathname, !!isCollapsed));
  }, [location]);

  return (
    <>
      <AntMenu
        data-testid={dataTestId}
        mode="inline"
        theme="dark"
        selectedKeys={menu.selectedKeys}
        openKeys={menu.openKeys}
        className={styles.menu}
        onClick={(e) => {
          // set redirect for popconfirm if booking in progress
          if (step && step < 4) {
            setModalRedirectKey(e.key);
          } else {
            e.key === '/logout' && handleLogout();
            step !== 5 && handleLeaveBooking();
            navigate(e.key);
          }
        }}
        onOpenChange={(openGroups: string[]) => {
          setMenu({ openKeys: openGroups });
        }}
        items={transformMenu(menuConfig)}
      />

      <Notepad />
    </>
  );
});
